import {
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { Subscription } from 'rxjs';
import { getBrowserLocales, getTimezone, resolveDefaultCountryCode } from '../../util';

class PhoneErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: AbstractControl | null): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}

interface CountryOption {
  code: CountryCode;
  name: string;
  callingCode: string;
  flag: string;
}

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PhoneInputComponent
  implements ControlValueAccessor, Validator, OnInit, OnDestroy
{
  @Input() label = 'Phone Number';
  @Input() required = false;

  @ViewChild('countrySelect') private countrySelect?: MatSelect;

  errorMatcher = new PhoneErrorStateMatcher();
  internalControl: UntypedFormControl;
  countryControl: UntypedFormControl;
  countries: CountryOption[] = [];
  filteredCountries: CountryOption[] = [];
  selectedCountry: CountryOption;
  countrySearchControl = new UntypedFormControl();

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;
  private subscriptions: Subscription[] = [];

  get countryCode(): string {
    return this.selectedCountry?.code || 'US';
  }

  get countryDetails(): { countryCallingCode: number; countryCode: string } {
    return {
      countryCallingCode: parseInt(
        this.selectedCountry?.callingCode || '1',
        10
      ),
      countryCode: this.selectedCountry?.code || 'US',
    };
  }

  ngOnInit(): void {
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    this.countries = getCountries()
      .map((code) => ({
        code,
        name: regionNames.of(code) || code,
        callingCode: getCountryCallingCode(code),
        flag: this.countryCodeToFlag(code),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    this.filteredCountries = this.countries;

    this.selectedCountry = this.resolveInitialCountry();
    this.countryControl = new UntypedFormControl(this.selectedCountry.code);

    const phoneValidator = (
      control: AbstractControl
    ): ValidationErrors | null => {
      const value = control.value?.toString()?.trim();
      if (!value) {
        return null;
      }
      const country = (this.selectedCountry?.code || 'US') as CountryCode;
      const pn = parsePhoneNumberFromString(value, country);
      return pn?.isValid() ? null : { invalidPhone: true };
    };
    const validators = this.required
      ? [Validators.required, phoneValidator]
      : [phoneValidator];
    this.internalControl = new UntypedFormControl('', validators);

    this.subscriptions.push(
      this.internalControl.valueChanges.subscribe((value) => {
        this.onChange(value);
      })
    );

    this.subscriptions.push(
      this.countryControl.valueChanges.subscribe((code: CountryCode) => {
        this.selectedCountry =
          this.countries.find((c) => c.code === code) || this.selectedCountry;
        // Re-validate with new country
        this.internalControl.updateValueAndValidity();
        this.onValidatorChange();
      })
    );

    // When internal control validity changes, notify the parent form
    this.subscriptions.push(
      this.internalControl.statusChanges.subscribe(() => {
        this.onValidatorChange();
      })
    );

    this.subscriptions.push(
      this.countrySearchControl.valueChanges.subscribe((search: string) => {
        const term = (search || '').trim().replace(/^\+/, '').toLowerCase();
        if (!term) {
          this.filteredCountries = this.countries;
          return;
        }
        this.filteredCountries = this.countries.filter(
          (c) =>
            c.name.toLowerCase().includes(term) ||
            c.code.toLowerCase().includes(term) ||
            c.callingCode.includes(term)
        );
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  setCountryCode(code: string): void {
    const country = this.countries.find((c) => c.code === code);
    if (country) {
      this.selectedCountry = country;
      this.countryControl.setValue(code, { emitEvent: false });
    }
  }

  writeValue(value: string): void {
    if (this.internalControl) {
      this.internalControl.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.internalControl?.disable();
    } else {
      this.internalControl?.enable();
    }
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (!this.internalControl) {
      return null;
    }
    return this.internalControl.errors;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  onBlur(): void {
    this.internalControl.markAsTouched();
    this.onTouched();
  }

  onCountryPanelToggled(opened: boolean): void {
    if (!opened) {
      return;
    }
    // mat-select scrolls the selected option into view once the overlay position
    // settles, which is after openedChange fires — so reset on the next frame.
    requestAnimationFrame(() => {
      const panel = this.countrySelect?.panel?.nativeElement;
      if (panel && this.countrySelect?.panelOpen) {
        panel.scrollTop = 0;
      }
    });
  }

  private resolveInitialCountry(): CountryOption {
    const detected = resolveDefaultCountryCode(
      getTimezone(),
      getBrowserLocales()
    );
    return (
      this.countries.find((c) => c.code === detected) ||
      this.countries.find((c) => c.code === 'US') ||
      this.countries[0]
    );
  }

  private countryCodeToFlag(code: string): string {
    const codePoints = code
      .toUpperCase()
      .split('')
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  }
}
